import { type AiUsageReport, formatUsd } from '@acasa/shared'
import { useState } from 'react'

/**
 * Billed amount per day of the month.
 *
 * ONE SERIES, because there is one number: the server sends the billed figure
 * and nothing it was computed from. An earlier version stacked purchase price
 * and markup — which disclosed both, moved in lockstep because the markup is a
 * fixed percentage, and failed the dark-mode separation check. A split by
 * feature would be two bars a day, and only if it passed that check; the two
 * tiles above already answer "which feature".
 *
 * Bars rather than a line: each day is a discrete amount that was spent, and a
 * line between two Tuesdays implies values on the days in between.
 */
export function AiUsageChart({ series }: { series: AiUsageReport['series'] }) {
  const [hover, setHover] = useState<number | null>(null)

  if (series.every((day) => day.calls === 0)) {
    return <p className="text-muted py-8 text-center text-sm">In diesem Monat keine KI-Nutzung.</p>
  }

  const max = Math.max(...series.map((day) => day.billedMicroUsd), 1)
  const active = hover === null ? null : series[hover]

  return (
    <div className="relative">
      {/* The only y-label worth printing is the peak; a full axis for one
          month of bars is furniture, not information. */}
      <div className="text-faint mb-1 text-right text-[10px]">{formatUsd(max)}</div>
      <div className="border-line relative border-t">
        <div className="flex h-32 items-end gap-[2px]">
          {series.map((day, index) => {
            const height = Math.max((day.billedMicroUsd / max) * 100, day.calls > 0 ? 2 : 0)
            return (
              <button
                key={day.day}
                type="button"
                // The whole column is the hit target — a 3px bar on a quiet day
                // is otherwise unhoverable.
                className="relative flex h-full flex-1 cursor-default items-end"
                onMouseEnter={() => setHover(index)}
                onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(index)}
                onBlur={() => setHover(null)}
                aria-label={`${dayLabel(day.day)}: ${formatUsd(day.billedMicroUsd)}, ${day.calls} Aufrufe`}
              >
                <span
                  className={`bg-brand block w-full rounded-t-[3px] transition-opacity ${
                    hover !== null && hover !== index ? 'opacity-40' : 'opacity-100'
                  }`}
                  style={{ height: `${height}%` }}
                />
              </button>
            )
          })}
        </div>
      </div>

      <div className="text-faint mt-1 flex justify-between text-[10px]">
        <span>{dayLabel(series[0]?.day ?? '')}</span>
        <span>{dayLabel(series.at(-1)?.day ?? '')}</span>
      </div>

      {active && (
        <div className="border-line bg-raised pointer-events-none absolute top-0 left-0 border px-2.5 py-1.5 text-[11px] shadow-sm">
          <div className="text-ink font-medium">{dayLabel(active.day)}</div>
          <div className="text-muted tnum mt-0.5 font-mono">{formatUsd(active.billedMicroUsd)}</div>
          <div className="text-faint mt-0.5">
            {active.calls} {active.calls === 1 ? 'Aufruf' : 'Aufrufe'}
          </div>
        </div>
      )}
    </div>
  )
}

/** `2026-10-15` → `15.10.` */
function dayLabel(day: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  return match ? `${match[3]}.${match[2]}.` : day
}
